import {billingInfoActions} from "../../store/actions/billingInformation/billingInformation";
import {geoActions} from "../../store/actions/geo/geo";
import {connect} from "react-redux";
import {compose} from "../../utils/compose";
import PropTypes from "prop-types";
import React, {useEffect, useRef, useState} from "react";
import {Button, CircularProgress, FormHelperText, Grid, Step, StepLabel, Stepper} from "@material-ui/core";
import {makeStyles} from "@material-ui/styles";
import {useTranslation} from "react-i18next";
import BillingInformationForm from "../BillingInformation/BillingInformationForm";
import InlineSpinner from "../UI/InlineSpinner/InlineSpinner";
import AddBraintreeCard from "../PaymentMethod/AddBraintreeCard";
import {httpClient, paymentMethodsBaseUrl, subscriptionBaseUrl, userInfoBaseUrl} from "../../http-client/http-client";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import PriceDetails from "../Subscription/PriceDetails";

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(2)
  },
  warning: {
    marginTop: theme.spacing(2),
    backgroundColor: '#d9d9d97d'
  },
  pricing: {
    padding: theme.spacing(2)
  }
}));

const BillingSetupWizard = ({
                              plan,
                              onCompleted,
                              billingInformation,
                              geo,
                              onFetchBillingInfo,
                              onFetchCountries,
                              access_token
                            }) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [paymentCollectionReady, setPaymentCollectionReady] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const paymentRef = useRef();
  const [estimate, setEstimate] = useState({
    fetching: true,
    data: null,
    total: null
  });

  useEffect(() => {
    onFetchBillingInfo()
  }, [onFetchBillingInfo])

  useEffect(() => {
    if (geo.countries && geo.countries.length === 0) {
      onFetchCountries()
    }
  }, []);

  const fetchEstimate = async () => {
    const url = `${subscriptionBaseUrl}/estimate/${plan.Id}`;

    try {
      const data = await httpClient.get(url, access_token);

      const invoice = data.Estimate.InvoiceEstimate === null ? data.Estimate.NextInvoiceEstimate : data.Estimate.InvoiceEstimate;
      setEstimate({
        fetching: false,
        data: invoice,
        error: null,
        total: plan.TrialPeriod ? 1 : invoice.Total.Amount
      });
    } catch (error) {
      setEstimate({
        fetching: false,
        data: null
      })
      setError(error.error.HumanError);
    }

  }

  function handleNext() {
    const nextStepIndex = activeStepIndex + 1;
    setActiveStepIndex(nextStepIndex);
  }

  function handlePrevious() {
    const prevStepIndex = activeStepIndex - 1;
    setActiveStepIndex(prevStepIndex);
  }

  function billingInfoUpdated() {
    onFetchBillingInfo();
    fetchEstimate();
    handleNext();
  }

  const addPaymentMethod = async (payload) => {
    return await httpClient.post(`${paymentMethodsBaseUrl}/addcard`, access_token, {
      Payload: payload
    });
  }

  const onPaymentSubmit = async () => {
    setSubmitting(true);

    try {
      const payload = await paymentRef.current.getPayload();
      setSubmitting(false);
      onCompleted(payload);

    } catch (error) {
      setSubmitting(false);
      setError(error.Message);
    }
  }

  function onPaymentCollectorReady(ready, error) {
    setPaymentCollectionReady(ready);
    setError(error);
  }

  function getCurrentComponent(index) {
    switch (index) {
      case 0:
        return (
          <>
            {billingInformation.fetching || geo.fetching ? <InlineSpinner size="small" align="center"/>
              : <BillingInformationForm current={billingInformation.data.BillingInformation}
                                        onSubmit={billingInfoUpdated}/>
            }
          </>
        );

      case 1:
        return (
          <>
            {!estimate.fetching && (
              <>
                <AddBraintreeCard innerRef={paymentRef} onReady={(ready, error) => onPaymentCollectorReady(ready, error)} amount={estimate.total} />
                {paymentCollectionReady && (
                  <>
                    <div className={classes.pricing}>
                      <PriceDetails invoiceEstimate={estimate.data}/>
                    </div>
                    <Grid container direction="row" alignItems="center" justify="flex-end" spacing={2}>
                      {error && (
                        <Grid item>
                          <FormHelperText className="Mui-error">{error}</FormHelperText>
                        </Grid>
                      )}
                      <Grid item>
                        <Button variant="outlined" color="primary" onClick={handlePrevious} disabled={submitting}>
                          {t('previous')}
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button variant="contained" color="primary" onClick={onPaymentSubmit} disabled={submitting}>
                          {submitting ? <CircularProgress size={20} color="inherit" style={{marginRight: "14px"}}/> : null}
                          {t('add')}
                        </Button>
                      </Grid>
                    </Grid>
                    <Grid container direction="row" alignItems="center" spacing={1} className={classes.warning}>
                      <Grid item>
                        <FontAwesomeIcon icon="info-circle"/>
                      </Grid>
                      <Grid item xs={true}>
                        {t('card3DsRedirectWarning')}
                      </Grid>
                    </Grid>
                  </>
                )}
              </>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Stepper activeStep={activeStepIndex}>
        <Step>
          <StepLabel>{t('billingInformationTitle')}</StepLabel>
        </Step>
        <Step>
          <StepLabel>{t('paymentMethod')}</StepLabel>
        </Step>
      </Stepper>
      <div className={classes.content}>
        {getCurrentComponent(activeStepIndex)}
      </div>
    </>
  );
}

BillingSetupWizard.propTypes = {
  plan: PropTypes.object.isRequired,
  onCompleted: PropTypes.func
}

const mapStateToProps = state => ({
  access_token: state.oidc.user.access_token,
  billingInformation: state.billingInformation,
  geo: state.geo
})

const mapDispatchToProps = dispatch => ({
  onFetchBillingInfo: () => dispatch(billingInfoActions.fetch()),
  onFetchCountries: () => dispatch(geoActions.fetchCountries())
})

var composed = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(BillingSetupWizard);

export default composed;